import { useEffect, useRef } from 'react';

/**
 * Run `callback` every `intervalMs`, but only while the browser tab is
 * visible. Ticks are skipped entirely while the tab is hidden, and one
 * catch-up call fires the moment the tab becomes visible again.
 *
 * Exists because background polls from parked tabs are real server load:
 * the 2026-08-26 access-log audit found hidden Dispatch Planner tabs
 * polling four /orders endpoints every 5s all night (~38% of that
 * family's 52k calls/day landed outside business hours).
 */
export function useVisiblePolling(
  callback: () => void,
  intervalMs: number,
  enabled: boolean,
): void {
  // Latest-callback ref so the interval never goes stale and callers don't
  // have to memoize; also keeps the effect off the callback dependency.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    const intervalId = setInterval(() => {
      if (!document.hidden) callbackRef.current();
    }, intervalMs);

    const onVisibilityChange = () => {
      if (!document.hidden) callbackRef.current();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, enabled]);
}
